import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShoppingBag, Sparkles, Minus, Plus, Loader2, Star, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import Footer from "../components/Footer";

export default function ProductDetail({ auth }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [cart, setCart] = useState([]);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    loadCart();
  }, [productId]);

  const loadCart = () => {
    const savedCart = localStorage.getItem('softRitualCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/store/products/${productId}`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
      } else if (response.status === 403) {
        toast.error("This product is exclusive to Inner Circle members");
        navigate("/store");
      } else {
        toast.error("Product not found");
        navigate("/store");
      }
    } catch (error) {
      console.error("Failed to fetch product:", error);
      toast.error("Failed to load product");
      navigate("/store");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/store/products/${productId}/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews);
        setAvgRating(data.average_rating);
        setTotalReviews(data.total_reviews);
      }
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    }
  };

  const submitReview = async () => {
    if (!auth?.user) {
      toast.error("Please sign in to leave a review");
      return;
    }
    
    setSubmittingReview(true);
    try {
      const response = await fetch(`${API}/store/products/${productId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product_id: productId,
          rating: reviewForm.rating,
          title: reviewForm.title || null,
          comment: reviewForm.comment || null
        })
      });
      
      if (response.ok) {
        toast.success("Review submitted successfully!");
        setShowReviewForm(false);
        setReviewForm({ rating: 5, title: "", comment: "" });
        fetchReviews();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to submit review");
      }
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const addToCart = () => {
    const existingItem = cart.find(item => item.product_id === product.product_id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item => 
        item.product_id === product.product_id 
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      newCart = [...cart, { 
        product_id: product.product_id, 
        name: product.name,
        price: product.user_price,
        image: product.images?.[0],
        quantity: quantity 
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('softRitualCart', JSON.stringify(newCart));
    toast.success(`${product.name} added to cart`);
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const StarRating = ({ rating, interactive = false, onRate }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => interactive && onRate?.(star)}
          className={interactive ? "cursor-pointer" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            className={`w-5 h-5 ${
              star <= rating 
                ? "fill-[#A87C6D] text-[#A87C6D]" 
                : "fill-none text-[#E6E2DD]"
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#A87C6D] animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  const maxQuantity = product.product_type === "physical" 
    ? Math.min(product.stock_quantity, 10) 
    : 10;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-[#E6E2DD]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/store" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Store</span>
          </Link>
          
          <Link to="/cart" className="relative">
            <Button variant="ghost" size="icon" className="text-[#6E6A66] hover:text-[#A87C6D]">
              <ShoppingBag className="w-5 h-5" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#A87C6D] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-6 py-8 w-full">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-[#F5F2ED] rounded-2xl overflow-hidden relative">
              {product.images?.[selectedImage] ? (
                <img 
                  src={product.images[selectedImage]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A8A4A0]">
                  <ShoppingBag className="w-16 h-16" />
                </div>
              )}
              {product.is_exclusive && (
                <div className="absolute top-4 left-4 px-4 py-2 bg-[#A87C6D] text-white text-sm rounded-full flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Inner Circle Exclusive
                </div>
              )}
            </div>
            
            {/* Thumbnail gallery */}
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === idx ? "border-[#A87C6D]" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-[#A8A4A0] capitalize mb-2">
                {product.category}
                {product.product_type === "digital" && " • Digital Download"}
              </p>
              <h1 
                className="text-3xl font-light text-[#2D2A26] mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {product.name}
              </h1>
              
              {/* Rating summary */}
              {totalReviews > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <StarRating rating={Math.round(avgRating)} />
                  <span className="text-[#6E6A66] text-sm">
                    {avgRating.toFixed(1)} ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
              )}
              
              <div className="flex items-center gap-3">
                <span className="text-2xl font-medium text-[#2D2A26]">
                  ${product.user_price.toFixed(2)}
                </span>
                {product.discount_applied && (
                  <>
                    <span className="text-lg text-[#A8A4A0] line-through">
                      ${product.price.toFixed(2)}
                    </span>
                    <span className="px-2 py-1 bg-[#7A9E7E]/20 text-[#7A9E7E] text-sm rounded-full">
                      {product.inner_circle_discount_percent}% off
                    </span>
                  </>
                )}
              </div>
            </div>

            <p className="text-[#6E6A66] leading-relaxed whitespace-pre-wrap">
              {product.description}
            </p>

            {/* Stock Status */}
            {product.product_type === "physical" && (
              <div>
                {product.stock_quantity === 0 ? (
                  <p className="text-[#C8553D]">Out of Stock</p>
                ) : product.stock_quantity <= 5 ? (
                  <p className="text-[#C8553D]">Only {product.stock_quantity} left in stock</p>
                ) : (
                  <p className="text-[#7A9E7E]">In Stock</p>
                )}
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="space-y-4 pt-4 border-t border-[#E6E2DD]">
              {product.stock_quantity > 0 || product.product_type === "digital" ? (
                <>
                  <div className="flex items-center gap-4">
                    <span className="text-[#6E6A66]">Quantity</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        className="w-8 h-8 rounded-full border border-[#E6E2DD] flex items-center justify-center text-[#6E6A66] hover:bg-[#F5F2ED]"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center text-[#2D2A26]">{quantity}</span>
                      <button
                        onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))}
                        className="w-8 h-8 rounded-full border border-[#E6E2DD] flex items-center justify-center text-[#6E6A66] hover:bg-[#F5F2ED]"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={addToCart}
                    data-testid="add-to-cart-btn"
                    className="w-full rounded-full py-6 bg-[#A87C6D] hover:bg-[#956d5f] text-white text-lg"
                  >
                    Add to Cart — ${(product.user_price * quantity).toFixed(2)}
                  </Button>
                </>
              ) : (
                <Button
                  disabled
                  className="w-full rounded-full py-6 bg-[#E6E2DD] text-[#A8A4A0] text-lg"
                >
                  Out of Stock
                </Button>
              )}
            </div>

            {/* Product Type Info */}
            {product.product_type === "digital" && (
              <div className="p-4 bg-[#F5F2ED] rounded-xl">
                <p className="text-sm text-[#6E6A66]">
                  <strong>Digital Product:</strong> You'll receive instant access to download after purchase.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16 border-t border-[#E6E2DD] pt-12">
          <div className="flex items-center justify-between mb-8">
            <h2 
              className="text-2xl font-light text-[#2D2A26]"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Customer Reviews
              {totalReviews > 0 && (
                <span className="ml-2 text-lg text-[#A8A4A0]">({totalReviews})</span>
              )}
            </h2>
            
            {auth?.user && !showReviewForm && (
              <Button
                onClick={() => setShowReviewForm(true)}
                variant="outline"
                className="rounded-full border-[#A87C6D] text-[#A87C6D] hover:bg-[#A87C6D] hover:text-white"
                data-testid="write-review-btn"
              >
                Write a Review
              </Button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-white border border-[#E6E2DD] rounded-2xl p-6 mb-8" data-testid="review-form">
              <h3 className="text-lg font-medium text-[#2D2A26] mb-4">Your Review</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#6E6A66] mb-2">Rating</label>
                  <StarRating 
                    rating={reviewForm.rating} 
                    interactive 
                    onRate={(r) => setReviewForm(f => ({ ...f, rating: r }))} 
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#6E6A66] mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="Summarize your experience"
                    className="w-full px-4 py-2 border border-[#E6E2DD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D]"
                    maxLength={100}
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-[#6E6A66] mb-2">Review (optional)</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                    placeholder="Share your thoughts about this product..."
                    rows={4}
                    className="w-full px-4 py-2 border border-[#E6E2DD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A87C6D]/20 focus:border-[#A87C6D] resize-none"
                    maxLength={500}
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={submitReview}
                    disabled={submittingReview}
                    className="rounded-full bg-[#A87C6D] hover:bg-[#956d5f] text-white"
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Submit Review
                  </Button>
                  <Button
                    onClick={() => setShowReviewForm(false)}
                    variant="outline"
                    className="rounded-full border-[#E6E2DD]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map(review => (
                <div 
                  key={review.review_id} 
                  className="bg-white border border-[#E6E2DD] rounded-2xl p-6"
                  data-testid={`review-${review.review_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <StarRating rating={review.rating} />
                        {review.verified_purchase && (
                          <span className="flex items-center gap-1 text-xs text-[#7A9E7E]">
                            <CheckCircle className="w-3 h-3" />
                            Verified Purchase
                          </span>
                        )}
                      </div>
                      {review.title && (
                        <h4 className="font-medium text-[#2D2A26]">{review.title}</h4>
                      )}
                    </div>
                    <div className="text-right text-sm text-[#A8A4A0]">
                      <p>{review.user_name}</p>
                      <p>{new Date(review.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {review.comment && (
                    <p className="text-[#6E6A66]">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-[#F5F2ED] rounded-2xl">
              <Star className="w-12 h-12 text-[#E6E2DD] mx-auto mb-4" />
              <p className="text-[#A8A4A0]">No reviews yet. Be the first to share your experience!</p>
              {!auth?.user && (
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    className="mt-4 rounded-full border-[#A87C6D] text-[#A87C6D]"
                  >
                    Sign in to Review
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
